/**
 * The HolidayMedia embed wire format. RETIRE-WITH-PRICECALCULATOR.
 *
 * ---------------------------------------------------------------------------
 * WHAT THIS FILE IS
 * ---------------------------------------------------------------------------
 *
 * A codec for the `postMessage` strings the old site's `widget.js` speaks, and
 * nothing else: no DOM, no React, no park. Everything here is derived from the
 * captured parent (`../public/widget.js`) and every claim in the comments was
 * checked by running that file, not by reading it — `test/protocol.spec.ts` and
 * `test/postMessageResults.spec.tsx` drive the real parent in jsdom.
 *
 * `docs/compat-protocol.md` is the prose version. Where this file disagrees with
 * it, this file is right, because it was tested; the report lists the four
 * places that happened.
 *
 * ---------------------------------------------------------------------------
 * THE PART THAT MATTERS: THE PARENT SILENTLY DROPS WHAT IT CANNOT PARSE
 * ---------------------------------------------------------------------------
 *
 * The parent checks `event.origin` and then runs one regex. A message that fails
 * the regex produces no effect, no exception and no log, on either side. Since
 * every tile starts hidden, a dropped message is indistinguishable from a
 * visitor who has not searched yet: the page looks exactly like its intended
 * initial state while the park stops selling.
 *
 * So `encodeChild` VERIFIES its own output against the parent's own regex before
 * returning it, and throws if it would not survive. A bug then arrives as a
 * failing test or a thrown error in our own console, instead of as a quiet
 * absence on somebody else's page. This is cheap because the regex is four
 * lines; it is worth it because the failure mode is invisible.
 */

/** Copied verbatim from the captured `widget.js`. Do not "improve" it. */
export const PARENT_REGEX =
  /^\s*vergarde-price-calculator-child\s*\((.+)\)\s*([^\s]*)(?:\s+(.+))?\s*$/;

/**
 * The literal source of `PARENT_REGEX`, so a test can assert it still occurs in
 * the captured parent byte for byte. A copied regex that has drifted from its
 * original is worse than no copy, and this is the only way to notice.
 */
export const PARENT_REGEX_SOURCE = String.raw`/^\s*vergarde-price-calculator-child\s*\((.+)\)\s*([^\s]*)(?:\s+(.+))?\s*$/`;

export const CHILD_PREFIX = 'vergarde-price-calculator-child';
export const PARENT_PREFIX = 'vergarde-price-calculator-parent';

/** The parent's own bounds on `scroll`. Outside them it drops the message. */
export const SCROLL_MIN = 1;
export const SCROLL_MAX = 3200;

/** Commands addressed to `all`. */
export type AllCommand = 'hide' | 'show' | 'list' | 'scroll' | 'beautify' | 'state';
/** Commands addressed to one key. */
export type KeyCommand = 'hide' | 'show' | 'pricetext' | 'url';

export interface ChildMessage {
  /** `all`, or one CMS key. */
  target: string;
  command: string;
  argument?: string;
}

/**
 * Encode one child → parent message, and refuse to produce one the parent
 * cannot read.
 *
 * Three ways to produce an unparseable message, all found by running the real
 * parent rather than by reading it:
 *
 *   1. **A `)` anywhere in the argument.** The target group is `(.+)` — greedy —
 *      followed by `\)`, so it backtracks to the LAST `)` in the whole string.
 *      `(zomerlodge) pricetext vanaf € 99 (7 nachten)` therefore parses as
 *      target `zomerlodge) pricetext vanaf € 99 (7 nachten` and command `''`,
 *      and is dropped. Confirmed: the span keeps its placeholder text.
 *   2. **A newline in the argument.** `(.+)` does not cross one and `$` is not
 *      multiline, so anything after a newline makes the whole message fail.
 *   3. **An empty argument.** For `pricetext` and `url` the parent guards on
 *      `argument &&`, so an empty string is a no-op rather than a clear. There
 *      is no way to blank a price; see `PostMessageResults` for what we do
 *      instead.
 *
 * Callers sanitise with `safeText` or `safeUrl` first; this only enforces.
 */
export function encodeChild(target: string, command: string, argument?: string): string {
  const message = argument === undefined
    ? `${CHILD_PREFIX} (${target}) ${command}`
    : `${CHILD_PREFIX} (${target}) ${command} ${argument}`;

  const parsed = parseChild(message);
  if (parsed === null || parsed.target !== target || parsed.command !== command
      || parsed.argument !== argument) {
    throw new Error(
      `compat-2027: refusing to send a message the old widget.js would silently drop: `
      + `${JSON.stringify(message)}. A ')' or a newline in the argument does this. `
      + `Sanitise with safeText() or safeUrl().`,
    );
  }
  return message;
}

/** The parent's parse, for the round-trip check and for tests. */
export function parseChild(message: string): ChildMessage | null {
  const m = PARENT_REGEX.exec(message);
  if (!m) return null;
  return { target: m[1]!, command: m[2]!, argument: m[3] };
}

/**
 * Make a price string safe to send.
 *
 * Parentheses are removed rather than substituted: a substitution would show
 * the visitor a character we invented, and there is no character that means "("
 * in Dutch. Nothing we format contains one — `Intl.NumberFormat('nl-NL', …)`
 * gives `€ 350,00` with a no-break space, which passes through untouched — so
 * this only fires on a caller-supplied label, which is exactly when a warning is
 * the useful part.
 *
 * Whitespace collapses because a newline is fatal and a run of spaces is not
 * worth preserving in a caption — but ONLY ASCII whitespace. `\s` in JavaScript
 * matches U+00A0, and `Intl.NumberFormat('nl-NL', …)` separates the euro sign
 * from the number with exactly that character, so a `/\s+/` collapse silently
 * rewrites every price on the page into a form that can line-break between the
 * symbol and the amount. Found by a test comparing against `Intl`'s own output.
 */
export function safeText(text: string, warn: (message: string) => void = defaultWarn): string {
  const collapsed = collapse(text);
  const stripped = collapse(collapsed.replace(/[()]/g, ''));
  if (stripped !== collapsed) {
    warn(
      `compat-2027: removed parentheses from ${JSON.stringify(text)} before sending it. `
      + `The old widget.js drops any message whose argument contains ')'.`,
    );
  }
  // The parent guards `argument &&`, so an empty argument is a silent no-op and
  // there is no way to blank a span. A caller that wanted to blank it gets a thin
  // space, spelled as an escape because an invisible literal in source is a
  // character nobody can see is there. `\u2009` is not ASCII whitespace, so
  // `collapse` above cannot turn it back into nothing.
  return stripped === '' ? '\u2009' : stripped;
}

/**
 * Make a URL safe to send.
 *
 * Unlike text, a URL can be fixed without lying: `(` and `)` are sub-delims and
 * `%28`/`%29` are the same URL by RFC 3986, so percent-encoding them keeps the
 * link working and gets the message past the regex. Verified against the real
 * parent: raw `)` drops the message, `%29` arrives and the anchor works.
 */
export function safeUrl(url: string): string {
  return url.replace(/[ \t\n\r\f\v]+/g, '').replace(/\(/g, '%28').replace(/\)/g, '%29');
}

/** ASCII whitespace only. See `safeText`. */
function collapse(text: string): string {
  return text.replace(/[ \t\n\r\f\v]+/g, ' ').replace(/^ +| +$/g, '');
}

/**
 * Round and clamp a measured size into the range the parent accepts.
 *
 * CLAMP, not validate. The parent drops an out-of-range `scroll` and keeps the
 * iframe at its previous size, which for a first paint is the 490px or 600px
 * minimum from the host page — so content taller than 3200px would be cut off
 * to 490 with no scrollbar to reveal it, because the host sets `scrolling="no"`.
 * 3200px of a 4000px list is a bad outcome; 490px of it is a broken page.
 */
export function clampScroll(width: number, height: number): [number, number] {
  return [clamp(width), clamp(height)];
}

function clamp(n: number): number {
  if (!Number.isFinite(n)) return SCROLL_MIN;
  return Math.min(SCROLL_MAX, Math.max(SCROLL_MIN, Math.ceil(n)));
}

export type ParentMessage =
  /** The parent's `history.state`. Only sent when it is truthy. */
  | { kind: 'state'; state: unknown }
  /** Which accommodations this page is about, and where the page is. */
  | { kind: 'objs'; origin: string; search: string; keys: string[] }
  /** Focus left the iframe. Polled every 800ms, sent once per departure. */
  | { kind: 'blur' };

/**
 * Parse a parent → child message.
 *
 * `objs` is `encodeURIComponent(origin) & encodeURIComponent(search) : k1,k2,…`.
 * Both halves are URI-encoded, so neither can contain a literal `&` or `:`, and
 * splitting on the first of each is unambiguous. The keys are NOT encoded — they
 * are `Object.keys(…).join(',')` of whatever the CMS author typed — so a key
 * containing a comma is unrepresentable and a key containing a colon would be
 * mis-split. Neither occurs in the live markers, which are config paths.
 */
export function parseParent(data: unknown): ParentMessage | null {
  if (typeof data !== 'string') return null;
  const trimmed = data.trim();
  if (!trimmed.startsWith(`${PARENT_PREFIX} `)) return null;
  const rest = trimmed.slice(PARENT_PREFIX.length + 1);

  if (rest === 'blur') return { kind: 'blur' };

  if (rest.startsWith('state ')) {
    const json = rest.slice('state '.length);
    try {
      return { kind: 'state', state: JSON.parse(json) };
    } catch {
      // The parent sends whatever `history.state` happened to be. Another
      // script on the page may have put something unserialisable in it, and
      // that is not a reason for our embed to stop working.
      return { kind: 'state', state: null };
    }
  }

  if (rest.startsWith('objs ')) {
    const payload = rest.slice('objs '.length);
    const amp = payload.indexOf('&');
    if (amp < 0) return null;
    const colon = payload.indexOf(':', amp);
    if (colon < 0) return null;
    return {
      kind: 'objs',
      origin: decodeOrEmpty(payload.slice(0, amp)),
      search: decodeOrEmpty(payload.slice(amp + 1, colon)),
      keys: payload.slice(colon + 1).split(',').map((k) => k.trim()).filter((k) => k !== ''),
    };
  }

  return null;
}

function decodeOrEmpty(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return '';
  }
}

function defaultWarn(message: string): void {
  // eslint-disable-next-line no-console
  console.warn(message);
}
